import React, { useEffect, useReducer } from 'react'
import { withStyles } from '@material-ui/core/styles'
import { FormattedMessage, useIntl } from 'react-intl'
import * as R from 'ramda'
import { usePlace } from 'commons/usePlace'
import hljs from 'highlight.js'
import 'highlight.js/styles/atom-one-light.css'
import ContentToolbarPage from 'app/components/ContentToolbarPage'
import LargeToolbar from 'app/components/LargeToolbar'
import {
  Container,
  Card,
  CardContent,
  Typography,
  CircularProgress,
} from '@material-ui/core'
import { copyToClipboard } from 'libs/clipboard'
import { useDispatch } from 'react-redux'
import MessageAlert from 'app/components/MessageAlert'
import effects from 'app/effects/effects'

const styles = (theme) => ({
  extendedIcon: {
    marginRight: theme.spacing(1),
  },
})

function useSubscriberWidget() {
  const initialState = {
    apiKey: undefined,
    isLoading: false,
    error: undefined,
  }
  function reducer(state, action) {
    const getToken = R.path([0, 'hash'])
    switch (action.type) {
      case 'LOAD_PLACE':
        return {
          apiKey: R.compose(getToken, R.prop('tokens'))(action.place),
        }
      case 'LOAD_API_KEY_START':
        return {
          ...state,
          isLoading: true,
        }
      case 'LOAD_API_KEY_SUCCESS':
        return {
          ...state,
          apiKey: getToken(action.tokens),
          isLoading: false,
          error: undefined,
        }
      case 'LOAD_API_KEY_FAILED':
        return {
          ...state,
          error: action.error,
          isLoading: false,
        }
      default:
        return state
    }
  }
  const [state, dispatch] = useReducer(reducer, initialState)
  const place = usePlace()

  useEffect(() => {
    dispatch({ type: 'LOAD_PLACE', place })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function generateNewToken() {
    dispatch({ type: 'LOAD_API_KEY_START' })
    effects.apis
      .generateIntegrationAPIToken()
      .then((tokens) => {
        dispatch({ type: 'LOAD_API_KEY_SUCCESS', tokens })
      })
      .catch((error) => {
        dispatch({ type: 'LOAD_API_KEY_FAILED', error })
      })
  }
  const languageDefault = 'en'
  const urlStep4 = `${process.env.REACT_APP_WEB_PUBLIC_MESSAGE_URL}/${languageDefault}/places/${place.id}/subscriber-widget?sdk=1`

  return [
    {
      ...state,
      code: {
        step1: `
          <script type="text/javascript">
            window.msAsyncInit = function () {
    MS.init({
      apiKey            : "${state.apiKey}",
      openWidgetSelector: ".subscribe-btn"
    });
  };
</script>
<script
  defer
  src="https://developers.messagespring.com/subscription/static/sdk.js"></script>
    `,
        step2: `<button class="subscribe-btn">Subscribe to us</button>`,
        step3: urlStep4,
      },
    },
    { generateNewToken },
  ]
}

const CodeSection = ({ code, language, onCopyCode }) => {
  const _code = R.trim(code)
  return (
    <div className="relative">
      <pre>
        <div
          className={`mt-12 p-8 pt-36 border border-gray-300 overflow-x-auto code-highlight rounded language-${language}`}
        >
          {_code}
        </div>
      </pre>
      <button
        className="absolute top-0 right-0 m-6 px-3 py-4 rounded border border-gray-600 text-gray-700 text-12 font-semibold bg-gray-100 hover:bg-gray-300 tracking-wider"
        onClick={() => onCopyCode(_code)}
      >
        <FormattedMessage id="integrations.subscriberWidget.copyCode" />
      </button>
    </div>
  )
}

const HiddenCode = () => (
  <pre>
    <div className="mt-12 p-8 border border-gray-300 code-highlight rounded language-html">
      <FormattedMessage id="integrations.subscriberWidget.toViewJavascript.title" />
    </div>
  </pre>
)

const HiddenCodeWidget = () => (
  <pre>
    <div className="mt-12 p-8 border border-gray-300 code-highlight rounded language-html">
      <FormattedMessage id="integrations.subscriberWidget.toViewJavascript.hidden.code.widget" />
    </div>
  </pre>
)

const Header = ({ children }) => <h2 className="text-16">{children}</h2>
const Body = ({ children }) => <p className="text-14 mt-8">{children}</p>

const SubscriberWidget = () => {
  const dispatch = useDispatch()
  const { formatMessage } = useIntl()
  const [{ apiKey, code, isLoading, error }, { generateNewToken }] =
    useSubscriberWidget()

  useEffect(() => {
    hljs.configure({ cssSelector: 'div.code-highlight' })
    hljs.highlightAll()
  }, [apiKey])

  function copyCode(code) {
    copyToClipboard(code).then(() => {
      dispatch(
        MessageAlert.showSuccess({
          message: formatMessage({
            id: 'integrations.subscriberWidget.codeCopied',
          }),
        }),
      )
    })
  }

  function generateApiTokenSection() {
    if (isLoading) {
      return (
        <span className="inline-block mt-12 px-3 py-4 rounded border border-gray-600 bg-gray-200">
          <span className="flex items-center space-x-8">
            <CircularProgress color="secondary" size={14} />
            <span className="text-gray-700 text-16 font-semibold tracking-wider">
              <FormattedMessage id="integrations.subscriberWidget.loading" />
            </span>
          </span>
        </span>
      )
    }
    return (
      <div>
        <div>
          <button
            className="mt-12 px-3 py-4 rounded border border-gray-600 text-gray-700 text-16 font-semibold hover:bg-gray-300 tracking-wider"
            onClick={generateNewToken}
          >
            <FormattedMessage id="integrations.subscriberWidget.generateApiToken.button" />
          </button>
        </div>
        {!R.isNil(error) && (
          <div className="mt-6 text-red">
            We encountered an issue generating the API token, please try again.
          </div>
        )}
      </div>
    )
  }

  return (
    <ContentToolbarPage
      isFitViewPort={false}
      contentToolbar={
        <LargeToolbar
          content={
            <>
              <Typography variant="h6">
                <FormattedMessage id="navigation.integrations.subscriberWidget.label" />
              </Typography>
            </>
          }
        />
      }
      content={
        <Container className="m-0 p-0">
          <Card>
            <CardContent className="p-40">
              <div>
                <Header>
                  <FormattedMessage id="integrations.subscriberWidget.overview.title" />
                </Header>
                <Body>
                  <FormattedMessage id="integrations.subscriberWidget.overview.description" />
                </Body>
              </div>

              <div className="mt-10 border-t border-gray-300"></div>

              <div className="mt-10">
                <Header>
                  <FormattedMessage id="integrations.subscriberWidget.step1.title" />
                </Header>
                <Body>
                  <FormattedMessage id="integrations.subscriberWidget.step1.description" />
                </Body>
                {R.isNil(apiKey) ? (
                  <>
                    <HiddenCodeWidget />
                    {generateApiTokenSection()}
                  </>
                ) : (
                  <CodeSection
                    language="javascript"
                    code={code.step1}
                    onCopyCode={copyCode}
                  />
                )}
              </div>

              <div className="mt-10 border-t border-gray-300"></div>

              <div className="mt-10">
                <Header>
                  <FormattedMessage id="integrations.subscriberWidget.step2.title" />
                </Header>
                <Body>
                  <FormattedMessage
                    id="integrations.subscriberWidget.step2.description"
                    values={{ code: (text) => <code>{text}</code> }}
                  />
                </Body>
                {R.isNil(apiKey) ? (
                  <HiddenCode />
                ) : (
                  <CodeSection
                    language="javascript"
                    code={code.step2}
                    onCopyCode={copyCode}
                  />
                )}
              </div>
              <div className="mt-10 border-t border-gray-300"></div>

              <div className="mt-10">
                <Header>
                  <FormattedMessage id="integrations.subscriberWidget.step3.title" />
                </Header>
                <Body>
                  <FormattedMessage id="integrations.subscriberWidget.step3.description" />
                </Body>
                {R.isNil(apiKey) ? (
                  <HiddenCode />
                ) : (
                  <CodeSection
                    language="javascript"
                    code={code.step3}
                    onCopyCode={copyCode}
                  />
                )}
              </div>
            </CardContent>
          </Card>
        </Container>
      }
    />
  )
}

export default withStyles(styles, { withTheme: true })(SubscriberWidget)
